import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, Wand2, LineChart, TrendingUp } from 'lucide-react';
const TABS = [{
  id: 'financial',
  title: 'Financial modeling',
  icon: <Calculator size={18} data-id="element-149" />,
  description: 'Build complex models with natural language instead of nested IF statements.',
  mockData: {
    title: 'Q3 Financial Model',
    rows: [['Revenue', '$1.2M', '$1.4M', '$1.8M'], ['COGS', '$300k', '$350k', '$400k'], ['Gross Margin', '75%', '75%', '77%']]
  }
}, {
  id: 'cleanup',
  title: 'Data cleanup',
  icon: <Wand2 size={18} data-id="element-150" />,
  description: 'Instantly normalize messy CRM exports with AI agents.',
  mockData: {
    title: 'Lead Enrichment',
    rows: [['Acme Corp', 'Software', 'San Francisco'], ['Global Tech', 'Hardware', 'New York'], ['Stark Ind.', 'Defense', 'Los Angeles']]
  }
}, {
  id: 'reporting',
  title: 'Reporting',
  icon: <LineChart size={18} data-id="element-151" />,
  description: 'Turn raw data into beautiful, shareable boards in seconds.',
  mockData: {
    title: 'Weekly Sales Report',
    rows: [['Product A', '450 units', '+12%'], ['Product B', '320 units', '-5%'], ['Product C', '890 units', '+24%']]
  }
}, {
  id: 'forecasting',
  title: 'Forecasting',
  icon: <TrendingUp size={18} data-id="element-152" />,
  description: 'Predict future trends based on historical data automatically.',
  mockData: {
    title: '2025 Forecast',
    rows: [['Q1 (Est)', '$2.1M', 'High Confidence'], ['Q2 (Est)', '$2.4M', 'Med Confidence'], ['Q3 (Est)', '$2.8M', 'Low Confidence']]
  }
}];
export function InActionTabs() {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const activeData = TABS.find(t => t.id === activeTab);
  return <section className="py-24 bg-zinc-950 border-t border-white/5" data-id="element-153">
      <div className="max-w-7xl mx-auto px-6" data-id="element-154">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-155">
          {/* Left: Tabs */}
          <div className="lg:col-span-5 space-y-8" data-id="element-156">
            <div data-id="element-157">
              <h2 className="text-3xl font-bold tracking-tight mb-4" data-id="element-158">
                See it in action
              </h2>
              <p className="text-zinc-400" data-id="element-159">
                Whether you're a founder modeling growth or an analyst cleaning
                data, Sheets2Board adapts to your workflow.
              </p>
            </div>

            <div className="flex flex-col gap-2" data-id="element-160">
              {TABS.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`text-left p-4 rounded-xl transition-all duration-200 border ${activeTab === tab.id ? 'bg-zinc-900 border-white/10 shadow-lg' : 'bg-transparent border-transparent hover:bg-zinc-900/50'}`} data-id="element-161">
                  <div className={`flex items-center gap-3 font-medium mb-1 ${activeTab === tab.id ? 'text-white' : 'text-zinc-400'}`} data-id="element-162">
                    {tab.icon}
                    {tab.title}
                  </div>
                  <p className={`text-sm ${activeTab === tab.id ? 'text-zinc-400' : 'text-zinc-500'}`} data-id="element-163">
                    {tab.description}
                  </p>
                </button>)}
            </div>
          </div>

          {/* Right: Mock UI */}
          <div className="lg:col-span-7" data-id="element-164">
            <div className="relative aspect-[4/3] bg-zinc-900/50 rounded-2xl border border-white/10 overflow-hidden shadow-2xl" data-id="element-165">
              {/* Mac window dots */}
              <div className="h-10 border-b border-white/10 flex items-center px-4 gap-2 bg-zinc-950/50" data-id="element-166">
                <div className="w-3 h-3 rounded-full bg-zinc-700" data-id="element-167" />
                <div className="w-3 h-3 rounded-full bg-zinc-700" data-id="element-168" />
                <div className="w-3 h-3 rounded-full bg-zinc-700" data-id="element-169" />
              </div>

              <div className="p-6" data-id="element-170">
                <AnimatePresence mode="wait" data-id="element-171">
                  <motion.div key={activeTab} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.3
                }} className="space-y-4" data-id="element-172">
                    <h3 className="text-lg font-semibold text-white mb-6" data-id="element-173">
                      {activeData?.mockData.title}
                    </h3>

                    <div className="border border-white/10 rounded-lg overflow-hidden bg-zinc-950" data-id="element-174">
                      {activeData?.mockData.rows.map((row, i) => <div key={i} className="flex border-b border-white/5 last:border-0" data-id="element-175">
                          {row.map((cell, j) => <div key={j} className={`flex-1 p-3 text-sm font-mono ${j === 0 ? 'text-zinc-300 bg-zinc-900/30' : 'text-zinc-400'} border-r border-white/5 last:border-0`} data-id="element-176">
                              {cell}
                            </div>)}
                        </div>)}
                    </div>

                    {/* Fake Chart Area */}
                    <div className="mt-6 h-32 border border-white/10 rounded-lg bg-zinc-950/50 flex items-end p-4 gap-2" data-id="element-177">
                      {[40, 70, 45, 90, 65, 100, 85].map((h, i) => <motion.div key={i} initial={{
                      height: 0
                    }} animate={{
                      height: `${h}%`
                    }} transition={{
                      duration: 0.5,
                      delay: i * 0.05
                    }} className="flex-1 bg-white/10 rounded-t-sm" data-id="element-178" />)}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}